/**
 * Software Metering Hub — nested views with /software-metering/* URL sync.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { HubPage, HubPanel, HubTabs } from '../ui/HubShell';
import { apiService } from '../../services/api';
import { SM_PATH_TO_VIEW, SM_VIEW_TO_PATH } from '../../config/navigation';
import { useAuth } from '../../contexts/AuthContext';

type ViewId = 'dashboard' | 'applications' | 'devices' | 'users' | 'policy' | 'reports';

function viewFromPath(): ViewId {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  return (SM_PATH_TO_VIEW[path] as ViewId) || 'dashboard';
}

function pushPath(view: ViewId) {
  const path = SM_VIEW_TO_PATH[view] || '/software-metering/dashboard';
  if (window.location.pathname !== path) {
    window.history.pushState({ smView: view }, '', path);
  }
}

function fmtDuration(sec: number | undefined) {
  const s = Math.max(0, Math.floor(sec || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

const TABS: { id: ViewId; label: string }[] = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'applications', label: 'Applications' },
  { id: 'devices', label: 'Devices' },
  { id: 'users', label: 'Users' },
  { id: 'policy', label: 'Policy' },
  { id: 'reports', label: 'Reports' },
];

const SoftwareMeteringHub: React.FC = () => {
  const { can } = useAuth();
  const [view, setView] = useState<ViewId>(viewFromPath());
  const [days, setDays] = useState(7);
  const [dash, setDash] = useState<any>(null);
  const [apps, setApps] = useState<any[]>([]);
  const [devices, setDevices] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [reports, setReports] = useState<any[]>([]);
  const [policy, setPolicy] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const go = useCallback((next: ViewId) => {
    setView(next);
    pushPath(next);
  }, []);

  useEffect(() => {
    const onPop = () => setView(viewFromPath());
    window.addEventListener('popstate', onPop);
    if (!window.location.pathname.startsWith('/software-metering')) {
      pushPath(view);
    }
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const reload = useCallback(async () => {
    setError(null);
    try {
      if (view === 'dashboard') setDash(await apiService.getSoftwareMeteringDashboard(days));
      if (view === 'applications') setApps(await apiService.getSoftwareMeteringApplications(Math.max(days, 30)));
      if (view === 'devices') setDevices(await apiService.getSoftwareMeteringDevices(Math.max(days, 30)));
      if (view === 'users') setUsers(await apiService.getSoftwareMeteringUsers(Math.max(days, 30)));
      if (view === 'reports') setReports(await apiService.getSoftwareMeteringReports(Math.max(days, 30)));
      if (view === 'policy' || view === 'dashboard') setPolicy(await apiService.getSoftwareMeteringPolicy());
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    }
  }, [view, days]);

  useEffect(() => {
    reload();
  }, [reload]);

  const cards = useMemo(() => {
    if (!dash) return [];
    return [
      ['Collection', dash.collection_enabled ? 'On' : 'Off'],
      ['Active time', fmtDuration(dash.total_active_seconds)],
      ['Devices', dash.active_devices],
      ['Users', dash.active_users],
      ['Applications', dash.distinct_applications],
      ['Events (range)', dash.event_count_7d],
    ];
  }, [dash]);

  return (
    <HubPage
      title="Software Metering"
      subtitle="Privacy-scoped application usage. Off by default; no URLs or keystrokes."
    >
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <HubTabs items={TABS.map((t) => ({ id: t.id, label: t.label }))} value={view} onChange={(id) => go(id as ViewId)} />
        <label className="text-sm text-slate-600 flex items-center gap-2">
          Days
          <select
            className="border rounded px-2 py-1 text-sm"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            {[7, 14, 30, 90].map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </label>
      </div>
      {error && <div className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>}
      {msg && <div className="mb-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm">{msg}</div>}

      {view === 'dashboard' && (
        <HubPanel>
          <h2 className="text-lg font-semibold mb-3">Usage overview</h2>
          {!policy?.enabled && (
            <p className="mb-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              Collection is disabled for this tenant. Enable it under Policy when ready.
            </p>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
            {cards.map(([label, value]) => (
              <div key={String(label)} className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-2xl font-semibold text-slate-900">{value ?? 0}</div>
              </div>
            ))}
          </div>
          <h3 className="font-medium mb-2">Top applications</h3>
          <ul className="space-y-1 text-sm">
            {(dash?.top_applications || []).map((a: any) => (
              <li key={a.name} className="flex justify-between border-b border-slate-100 py-1">
                <span>{a.name}</span>
                <span className="text-slate-500">{fmtDuration(a.active_seconds)}</span>
              </li>
            ))}
            {!dash?.top_applications?.length && <li className="text-slate-400">No usage data yet</li>}
          </ul>
        </HubPanel>
      )}

      {view === 'applications' && (
        <HubPanel>
          <h2 className="text-lg font-semibold mb-3">Applications</h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b">
                <th className="py-2">Application</th>
                <th>Process</th>
                <th>Active</th>
                <th>Devices</th>
                <th>Users</th>
              </tr>
            </thead>
            <tbody>
              {apps.map((a) => (
                <tr key={`${a.process_name}-${a.application_name}`} className="border-b border-slate-100">
                  <td className="py-2">{a.application_name}</td>
                  <td>{a.process_name}</td>
                  <td>{fmtDuration(a.total_active_seconds)}</td>
                  <td>{a.device_count}</td>
                  <td>{a.user_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!apps.length && <p className="text-slate-400 text-sm mt-3">No data</p>}
        </HubPanel>
      )}

      {view === 'devices' && (
        <HubPanel>
          <h2 className="text-lg font-semibold mb-3">Devices</h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b">
                <th className="py-2">Device</th>
                <th>Active</th>
                <th>Apps</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((d) => (
                <tr key={d.device_id} className="border-b border-slate-100">
                  <td className="py-2">{d.device_name}</td>
                  <td>{fmtDuration(d.total_active_seconds)}</td>
                  <td>{d.application_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!devices.length && <p className="text-slate-400 text-sm mt-3">No data</p>}
        </HubPanel>
      )}

      {view === 'users' && (
        <HubPanel>
          <h2 className="text-lg font-semibold mb-3">Users</h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b">
                <th className="py-2">User</th>
                <th>Active</th>
                <th>Apps</th>
                <th>Devices</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_name} className="border-b border-slate-100">
                  <td className="py-2">{u.user_name}</td>
                  <td>{fmtDuration(u.total_active_seconds)}</td>
                  <td>{u.application_count}</td>
                  <td>{u.device_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!users.length && <p className="text-slate-400 text-sm mt-3">No data</p>}
        </HubPanel>
      )}

      {view === 'policy' && policy && (
        <HubPanel>
          <h2 className="text-lg font-semibold mb-3">Collection policy</h2>
          <p className="text-sm text-slate-600 mb-4">
            Defaults protect privacy: collection off, window titles off/masked, no URLs.
          </p>
          <div className="grid md:grid-cols-2 gap-4 text-sm max-w-3xl">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!policy.enabled}
                disabled={!can('software_metering.all.policy_edit') || busy}
                onChange={(e) => setPolicy({ ...policy, enabled: e.target.checked })}
              />
              Enable collection
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!policy.collect_window_title}
                disabled={!can('software_metering.all.policy_edit') || busy}
                onChange={(e) => setPolicy({ ...policy, collect_window_title: e.target.checked })}
              />
              Collect window titles
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!policy.mask_window_title}
                disabled={!can('software_metering.all.policy_edit') || busy}
                onChange={(e) => setPolicy({ ...policy, mask_window_title: e.target.checked })}
              />
              Mask window titles
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!policy.collect_executable_path}
                disabled={!can('software_metering.all.policy_edit') || busy}
                onChange={(e) => setPolicy({ ...policy, collect_executable_path: e.target.checked })}
              />
              Collect executable path
            </label>
            <label className="flex flex-col gap-1">
              Sample interval (sec)
              <input
                type="number"
                className="border rounded px-2 py-1"
                value={policy.sample_interval_seconds}
                disabled={!can('software_metering.all.policy_edit') || busy}
                onChange={(e) => setPolicy({ ...policy, sample_interval_seconds: Number(e.target.value) })}
              />
            </label>
            <label className="flex flex-col gap-1">
              Upload interval (sec)
              <input
                type="number"
                className="border rounded px-2 py-1"
                value={policy.upload_interval_seconds}
                disabled={!can('software_metering.all.policy_edit') || busy}
                onChange={(e) => setPolicy({ ...policy, upload_interval_seconds: Number(e.target.value) })}
              />
            </label>
            <label className="flex flex-col gap-1">
              Idle threshold (sec)
              <input
                type="number"
                className="border rounded px-2 py-1"
                value={policy.idle_threshold_seconds}
                disabled={!can('software_metering.all.policy_edit') || busy}
                onChange={(e) => setPolicy({ ...policy, idle_threshold_seconds: Number(e.target.value) })}
              />
            </label>
            <label className="flex flex-col gap-1 md:col-span-2">
              Excluded processes (comma-separated)
              <input
                type="text"
                className="border rounded px-2 py-1"
                value={(policy.excluded_processes || []).join(', ')}
                disabled={!can('software_metering.all.policy_edit') || busy}
                onChange={(e) =>
                  setPolicy({
                    ...policy,
                    excluded_processes: e.target.value
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean),
                  })
                }
              />
            </label>
          </div>
          {can('software_metering.all.policy_edit') && (
            <button
              className="mt-4 px-4 py-2 rounded bg-slate-900 text-white text-sm disabled:opacity-50"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                setMsg(null);
                try {
                  const saved = await apiService.updateSoftwareMeteringPolicy({
                    enabled: policy.enabled,
                    sample_interval_seconds: policy.sample_interval_seconds,
                    upload_interval_seconds: policy.upload_interval_seconds,
                    idle_threshold_seconds: policy.idle_threshold_seconds,
                    collect_window_title: policy.collect_window_title,
                    mask_window_title: policy.mask_window_title,
                    collect_executable_path: policy.collect_executable_path,
                    collect_idle_time: policy.collect_idle_time,
                    excluded_processes: policy.excluded_processes,
                    included_processes: policy.included_processes,
                    retention_days: policy.retention_days,
                  });
                  setPolicy(saved);
                  setMsg('Policy saved');
                } catch (e: any) {
                  setError(e?.message || 'Save failed');
                } finally {
                  setBusy(false);
                }
              }}
            >
              Save policy
            </button>
          )}
        </HubPanel>
      )}

      {view === 'reports' && (
        <HubPanel>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">Reports</h2>
            {can('software_metering.all.export') && (
              <button
                className="text-sm px-3 py-1.5 rounded border border-slate-300"
                onClick={async () => {
                  try {
                    await apiService.exportSoftwareMetering(Math.max(days, 30));
                    setMsg('Export downloaded');
                  } catch (e: any) {
                    setError(e?.message || 'Export failed');
                  }
                }}
              >
                Export CSV
              </button>
            )}
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b">
                <th className="py-2">Date</th>
                <th>Device</th>
                <th>User</th>
                <th>Application</th>
                <th>Active</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r, i) => (
                <tr key={`${r.usage_date}-${r.device_id}-${r.process_name}-${i}`} className="border-b border-slate-100">
                  <td className="py-2">{r.usage_date}</td>
                  <td>{r.device_name}</td>
                  <td>{r.user_name || '—'}</td>
                  <td>{r.application_name}</td>
                  <td>{fmtDuration(r.total_active_seconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!reports.length && <p className="text-slate-400 text-sm mt-3">No data</p>}
        </HubPanel>
      )}
    </HubPage>
  );
};

export default SoftwareMeteringHub;
